import React, { createContext, useEffect, useState, useRef } from "react";
import { withAuthContext } from "./Auth";
import axios from "axios";
import swal from "sweetalert";
import { useCappedRetry } from "utils/retry";

export const PropertyContext = createContext();

export const withPropertyContext = (Component) => (props) =>
	(
		<PropertyContext.Consumer>
			{(value) => <Component {...value} {...props} />}
		</PropertyContext.Consumer>
	);

const FILTER_KEY_MAP = {
	Tower: "tower",
	tower: "tower",
	towers: "tower",
	Floor: "floor",
	floor: "floor",
	floors: "floor",
	Category: "category",
	category: "category",
	categories: "category",
	FlatNo: "flatNo",
	flatNo: "flatNo",
	flatNos: "flatNo"
};

const normalizeFilterKey = (key = "") => FILTER_KEY_MAP[key] || key;

const sanitizeFilters = (filters = {}) => {
	const cleaned = {};
	Object.entries(filters || {}).forEach(([key, value]) => {
		if (value === undefined || value === null || value === "") return;
		const normalizedKey = normalizeFilterKey(key);
		if (Array.isArray(value)) {
			if (value.length > 0) cleaned[normalizedKey] = value.join(",");
		} else {
			cleaned[normalizedKey] = value;
		}
	});
	return cleaned;
};

const PropertyProvider = ({ children, Token, CheckToken }) => {
	const [AllProperty, setAllProperty] = useState([]);
	const [errorAllProperty, setErrorAllProperty] = useState("");
	const isFetchingRef = useRef(false);
	const lastFiltersRef = useRef({});
	const retryGetProperties = useCappedRetry();
	const retryAddPropertyToUser = useCappedRetry();
	const retryRemovePropertyToUser = useCappedRetry();

	const [LoadingProperty, setLoadingProperty] = useState(false);
	const [pagination, setPagination] = useState({
		total: 0,
		page: 1,
		limit: 10,
		totalPages: 0
	});

	const GetProperties = (page = 1, limit = 10, filters = {}) => {
		// Prevent duplicate API calls
		if (isFetchingRef.current) return;

		if (Token || localStorage.getItem("token")) {
			isFetchingRef.current = true;
			setLoadingProperty(true);
			const sanitizedFilters = sanitizeFilters(filters);
			lastFiltersRef.current = sanitizedFilters;
			axios
				.get(`${process.env.REACT_APP_PUBLIC_PATH}/GetAllProperty`, {
					params: {
						page,
						limit,
						...sanitizedFilters
					},
					headers: {
						Authorization: `Bearer ${Token || localStorage.getItem("accessToken") || localStorage.getItem("token")}`
					}
				})
				.then((res) => {
					setLoadingProperty(false);
					if (res?.data?.status == 200) {
						setAllProperty(res?.data?.data);
						if (res?.data?.pagination) {
							setPagination(res?.data?.pagination);
						}
					} else {
						setErrorAllProperty(res?.data?.message);
					}
				})
				.catch((err) => {
					setLoadingProperty(false);
					setErrorAllProperty(err?.message);
				})
				.finally(() => {
					isFetchingRef.current = false;
				});
		} else {
			setLoadingProperty(false);
			retryGetProperties(() => {
				CheckToken();
				GetProperties(page, limit, lastFiltersRef.current);
			});
		}
	};

	const AddPropertyToUser = ({ userId, propertyId, Type, navigate }) => {
		if (LoadingProperty) return; // Prevent duplicate calls
		
		if (Token || localStorage.getItem("token")) {
			setLoadingProperty(true);
			axios
				.post(
					`${process.env.REACT_APP_PUBLIC_PATH}/Property-User`,
					{
						property: propertyId,
						user: userId,
						Type: Type ? Type : "owner"
					},
					{
						headers: {
							Authorization: `Bearer ${Token || localStorage.getItem("accessToken") || localStorage.getItem("token")}`
						}
					}
				)
				.then((res) => {
					setLoadingProperty(false);
					if (res?.data?.status == 200) {
						swal({
							text: "Property was added to user",
							button: {
								text: "Ok",
								closeModal: true
							},
							icon: res?.data?.status == 200 ? "success" : "error",
							time: 3000
						});
						GetProperties(pagination?.page || 1, pagination?.limit || 10, lastFiltersRef.current);
						navigate("/User");
					} else {
						swal({
							text: "There was some Error",
							button: {
								text: "Ok",
								closeModal: true
							},
							icon: "error",
							time: 3000
						});
					}
				})
				.catch((err) => {
					setLoadingProperty(false);
					swal({
						text: err?.response?.data?.message
							? err?.response?.data?.message
							: "There was some Error",
						button: {
							text: "Ok",
							closeModal: true
						},
						icon: "error",
						time: 3000
					});
				});
		} else {
			setLoadingProperty(false);
			retryAddPropertyToUser(() => {
				CheckToken();
				AddPropertyToUser({ userId, propertyId, navigate });
			});
		}
	};

	const RemovePropertyToUser = ({ userId, propertyId, GetUsers }) => {
		if (LoadingProperty) return; // Prevent duplicate calls
		
		if (Token || localStorage.getItem("token")) {
			setLoadingProperty(true);
			axios
				.post(
					`${process.env.REACT_APP_PUBLIC_PATH}/Remove-Property-User`,
					{
						property: propertyId,
						user: userId
					},
					{
						headers: {
							Authorization: `Bearer ${Token || localStorage.getItem("accessToken") || localStorage.getItem("token")}`
						}
					}
				)
				.then((res) => {
					setLoadingProperty(false);
					if (res?.data?.status == 200) {
						swal({
							text: "Property was removed to user",
							button: {
								text: "Ok",
								closeModal: true
							},
							icon: res?.data?.status == 200 ? "success" : "error",
							time: 3000
						});
						GetUsers();
					} else {
						swal({
							text: "There was some Error",
							button: {
								text: "Ok",
								closeModal: true
							},
							icon: "error",
							time: 3000
						});
					}
				})
				.catch((err) => {
					setLoadingProperty(false);
					swal({
						text: err?.response?.data?.message
							? err?.response?.data?.message
							: "There was some Error",
						button: {
							text: "Ok",
							closeModal: true
						},
						icon: "error",
						time: 3000
					});
				});
		} else {
			setLoadingProperty(false);
			retryRemovePropertyToUser(() => {
				CheckToken();
				RemovePropertyToUser({ userId, propertyId, GetUsers });
			});
		}
	};

	useEffect(() => {
		if (Token) {
			GetProperties(1, 10, lastFiltersRef.current);
		}
	}, [Token]);
	return (
		<PropertyContext.Provider
			value={{
				AllProperty,
				errorAllProperty,
				GetProperties,
				AddPropertyToUser,
				RemovePropertyToUser,
				LoadingProperty,
				setLoadingProperty,
				pagination
			}}
		>
			{children}
		</PropertyContext.Provider>
	);
};

export default withAuthContext(PropertyProvider);
